import React, { useRef, useState } from 'react'
import Modal from '../../../components/modal/modal.component'
import { Options } from '../start-recording.page'
import classes from '../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import { useTranslation } from 'react-i18next';
import { GreenTick, Pdf, TrashIcon } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import globalClasses from '../../styles.module.scss'
import {
  apiService,
  ProcessingPipeline,
  ProcessingStep,
  VideoProcessingOptions,
} from '../../../services/api.service';

const cn = createCn('file-modal', classes);

type ProcessingStage = 'idle' | 'uploading' | 'processing' | 'done' | 'error';
type MediaMode = 'audio' | 'video';

const STAGE_LABELS: Record<string, string> = {
  saved: 'Файл сохранён',
  converting: 'Подготовка аудио',
  extract_audio: 'Извлечение аудиодорожки',
  diarization: 'Диаризация (разделение по голосам)',
  stt: 'Транскрипция',
  gender: 'Детекция пола спикеров',
  tone: 'Анализ тональности',
  video_vlm: 'Анализ видеоконтента (Qwen VLM)',
  protocol: 'Генерация протокола (Qwen)',
  summary: 'Генерация конспекта (Qwen)',
  insights: 'AI-аналитика и задачи',
  done: 'Готово',
};

const DEFAULT_VIDEO_OPTIONS: VideoProcessingOptions = {
  gender: true,
  tone: true,
  slides: false,
  ai_protocol: true,
  ai_summary: true,
  ai_tasks: true,
  translate_to: null,
};

const AUDIO_ACCEPT = '.mp3,.wav,.ogg,.m4a,.mp4,.webm,.m4v,.MP4';
const VIDEO_ACCEPT = '.mp4,.webm,.m4v,.mov,.mkv,.avi,.MP4';

function nameFromFile(f: File): string {
  const base = f.name.replace(/\.[^.]+$/, '').trim();
  return base || f.name.trim();
}

const AUDIO_PIPELINE_OPTIONS = [
  ['deepgram', 'Диаризация по спикерам', 'Локальная диаризация и транскрипция за один проход. Рекомендуется.'],
  ['pyannote', 'Pyannote (посегментно)', 'Локальная диаризация → транскрипция по каждому сегменту. Медленно (~15 мин).'],
  ['fast', 'Быстрый (без диаризации)', 'Весь файл целиком → протокол. Без разделения по спикерам.'],
] as const;

const PIPELINE_LABELS: Record<string, string> = Object.fromEntries(
  AUDIO_PIPELINE_OPTIONS.map(([value, title]) => [value, title]),
);

const FileModal = ({ show, setSelectedOption }: { show: boolean, setSelectedOption: (option: Options | null) => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [mediaMode, setMediaMode] = useState<MediaMode>('audio');
  // Режим обработки не выбирается пользователем: всегда самый грамотный —
  // локальная диаризация + транскрипция за один проход (+ авто-казахский, аналитика).
  const pipeline: ProcessingPipeline = 'deepgram';
  const [videoOptions, setVideoOptions] = useState<VideoProcessingOptions>(DEFAULT_VIDEO_OPTIONS);
  const [stage, setStage] = useState<ProcessingStage>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processProgress, setProcessProgress] = useState(0);
  const [processMessage, setProcessMessage] = useState('');
  const [processSteps, setProcessSteps] = useState<ProcessingStep[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetForm = () => {
    setFile(null);
    setUploadProgress(0);
    setProcessProgress(0);
    setProcessMessage('');
    setProcessSteps([]);
    setStage('idle');
    setErrorMessage('');
    setName('');
    setMediaMode('audio');
    setVideoOptions(DEFAULT_VIDEO_OPTIONS);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pickFile = (next: File) => {
    setFile(next);
    setName(prev => (prev.trim() ? prev : nameFromFile(next)));
    setStage('idle');
    setUploadProgress(0);
    setProcessProgress(0);
    setProcessMessage('');
    setProcessSteps([]);
    setErrorMessage('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) pickFile(e.target.files[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) pickFile(e.dataTransfer.files[0]);
  };

  const cancelUpload = () => resetForm();

  const toggleVideoOption = (key: keyof VideoProcessingOptions) => {
    setVideoOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAdd = async () => {
    const meetingName = name.trim() || (file ? nameFromFile(file) : '');
    if (!file || !meetingName) return;

    setStage('uploading');
    setUploadProgress(0);
    setProcessProgress(0);
    setProcessMessage('');
    setProcessSteps([]);
    setErrorMessage('');

    try {
      // Только загружаем файл на сервер. Обработку не ждём — она идёт в очереди,
      // прогресс (процент/этапы/ETA) пользователь смотрит в разделе «Записи».
      mediaMode === 'video'
        ? await apiService.processVideo(file, meetingName, [], {
            videoOptions,
            onUploadProgress: setUploadProgress,
          })
        : await apiService.processAudio(file, meetingName, [], {
            pipeline,
            onUploadProgress: setUploadProgress,
          });

      setUploadProgress(100);
      setStage('done');

      toast(<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <GreenTick />
        Файл загружен и поставлен в очередь. Прогресс обработки — в разделе «Записи».
      </div>);

      setSelectedOption(null);
      resetForm();
      navigate('/files');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка обработки';
      setStage('error');
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const isProcessing = ['uploading', 'processing'].includes(stage);
  const accept = mediaMode === 'video' ? VIDEO_ACCEPT : AUDIO_ACCEPT;
  const canSubmit = Boolean(file);
  const enabledVideoOptions = Object.entries(videoOptions).filter(
    ([k, v]) => v === true && k !== 'translate_to',
  ).length;

  const ProgressBar = ({
    label,
    percent,
    hint,
  }: {
    label: string;
    percent: number;
    hint?: string;
  }) => (
    <div className={cn('progress-group')}>
      <div className={cn('progress-label')}>
        <span>{label}</span>
        <span>{percent}%</span>
      </div>
      {hint && <div className={cn('progress-hint')}>{hint}</div>}
      <div className={cn('progress-wrapper')}>
        <div className={cn('progress-bar')} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      handleClose={() => !isProcessing && setSelectedOption(null)}
      header={<div className={classes['modal-heading']}>
        {t('start-recording.file.modal.heading')}
      </div>}
      footer={
        <div className={cn('footer')}>
          {isProcessing && (
            <span className={cn('footer-status')}>
              {stage === 'uploading' ? 'Загрузка на сервер…' : (processMessage || 'Обработка…')}
            </span>
          )}
          <button
            type="button"
            className={cn('submit-btn', { processing: isProcessing })}
            onClick={handleAdd}
            disabled={!canSubmit || isProcessing}
            title={!file ? 'Выберите файл' : undefined}
          >
            {isProcessing
              ? (stage === 'uploading' ? 'Загрузка…' : `${processProgress}%`)
              : t('action.add')}
          </button>
        </div>
      }
      style={{
        padding: 0,
        borderRadius: '1.25rem',
        width: '37.5rem',
        maxWidth: 'min(37.5rem, 95vw)',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div className={cn('shell')}>
      <div className={cn('scroll')}>
        <div className={classes['modal-body']}>
          {isProcessing && (
            <div className={cn('processing-banner')}>
              <span className={cn('processing-banner-title')}>
                {stage === 'uploading' ? 'Загрузка файла' : 'Обработка встречи'}
              </span>
              <span className={cn('processing-banner-hint')}>
                {mediaMode === 'video'
                  ? `${enabledVideoOptions} опций · видео ${file?.name ?? ''}`
                  : `Режим ${PIPELINE_LABELS[pipeline] ?? pipeline} · ${file?.name ?? ''}`}
              </span>
            </div>
          )}

          <div className={globalClasses['form-group']}>
            <label className={cn('label')}>{t('start-recording.file.modal.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('start-recording.file.modal.name-placeholder') || ''}
              className={classes['modal-input']}
              disabled={isProcessing}
            />
          </div>

          {!isProcessing && (
            <>
              <div className={cn('mode-tabs')}>
                <button
                  type="button"
                  className={cn('mode-tab', { active: mediaMode === 'audio' })}
                  disabled={isProcessing}
                  onClick={() => { setMediaMode('audio'); setFile(null); }}
                >
                  Аудио
                </button>
                <button
                  type="button"
                  className={cn('mode-tab', { active: mediaMode === 'video' })}
                  disabled={isProcessing}
                  onClick={() => { setMediaMode('video'); setFile(null); }}
                >
                  Видео (Teams)
                </button>
              </div>

              {mediaMode === 'video' && (
                <div className={cn('diarize-option')}>
                  <label className={cn('label')}>Обработка видео</label>
                  {([
                    ['gender', 'Детекция пола спикеров', 'Анализ голоса каждого участника'],
                    ['tone', 'Тональность голоса', 'Эмоциональный окрас речи по спикерам'],
                    ['slides', 'Распознавание слайдов', 'Qwen VLM: ключевые кадры, OCR, презентации и screen share'],
                    ['ai_protocol', 'AI-протокол', 'Структурированный протокол встречи'],
                    ['ai_summary', 'Конспект', 'Краткое содержание по темам'],
                    ['ai_tasks', 'TODO List / задачи', 'Поручения, ответственные, дедлайны'],
                  ] as const).map(([key, title, hint]) => (
                    <label key={key} className={cn('diarize-label')}>
                      <input
                        type="checkbox"
                        checked={Boolean(videoOptions[key as keyof VideoProcessingOptions])}
                        onChange={() => toggleVideoOption(key as keyof VideoProcessingOptions)}
                        disabled={isProcessing}
                      />
                      <span>
                        <strong>{title}</strong>
                        {` — ${hint}`}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}

          <div
            className={cn('upload-area', { compact: isProcessing })}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            style={{ borderColor: file ? 'var(--color-gray-light)' : 'var(--color-primary)' }}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept={accept}
              disabled={isProcessing}
            />

            {!file ? (
              <>
                <p>
                  <span
                    className={cn('upload-link')}
                    onClick={() => !isProcessing && fileInputRef.current?.click()}
                  >
                    {t('start-recording.file.modal.upload')}
                  </span>{' '}
                  {mediaMode === 'video'
                    ? 'видеофайл (Teams, презентация)'
                    : t('start-recording.file.modal.upload-text')}
                </p>
                <div className={cn('upload-info')}>
                  {mediaMode === 'audio' ? (
                    <>
                      <p>{t('start-recording.file.modal.audio-formats')}</p>
                      <p>{t('start-recording.file.modal.video-formats')}</p>
                    </>
                  ) : (
                    <p>Форматы видео: .mp4, .webm, .m4v, .mov, .mkv, .avi</p>
                  )}
                </div>
              </>
            ) : (
              <div className={cn('file-info')}>
                <div className={cn('file-header')}>
                  <div className={cn('file-icon', { video: mediaMode === 'video' })}>
                    {mediaMode === 'video' ? '▶' : <Pdf />}
                  </div>
                  <div className={cn('file-name')}>
                    <span>{file.name}</span>
                    <span>
                      {file.size < 1024 * 1024
                        ? `${(file.size / 1024).toFixed(2)} KB`
                        : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    </span>
                  </div>
                  {!isProcessing && <TrashIcon onClick={cancelUpload} />}
                </div>

                {isProcessing && (
                  <div className={cn('progress-stack')}>
                    <ProgressBar label="Загрузка файла" percent={uploadProgress} />
                    {(stage === 'processing' || processProgress > 0) && (
                      <ProgressBar
                        label="Обработка"
                        percent={processProgress}
                        hint={processMessage}
                      />
                    )}
                    {processSteps.length > 0 && (
                      <ul className={cn('step-list')}>
                        {processSteps.map(step => (
                          <li
                            key={step.id}
                            className={cn('step-item', { [step.status]: true })}
                          >
                            <span className={cn('step-dot')} />
                            {step.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {stage === 'error' && (
                  <p className={cn('error-text')}>
                    {errorMessage}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </Modal>
  )
}

export default FileModal;
